import { useState, useCallback, useEffect } from "react";

import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  Divider,
  FormLabel,
  FormGroup,
  TextField,
  Typography,
} from "@material-ui/core";

import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import HeaderClientes from "components/_refactor/headers/clients";
import StructureFields from "components/_refactor/fields/structure";
import TableEstoque from "./table/TableEstoque";
import DialogUploadEstoque from "../../components/Dialogs/DialogUploadEstoque";
import useForm from "hooks/form";
import componentStyles from "assets/theme/views/admin/react-bs-table";
import Api from "lib/api/httpRequestHelper";
import MaskAndValid from "utils/maskAndValid";
import UtilsFncs from "utils/UtilsFncs";
import ClearButton from "components/_refactor/button/clear";
import LoadingData from "components/Loading/LoadingData";
import useLogs from "hooks/logs";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import { IsMobile900, IsMobile } from "utils/is-mobile";
import InputText from "components/_refactor/text/input";

const useStyles = makeStyles(componentStyles);

const useCustomStyles = makeStyles({
  fields: {
    display: "flex",
    alignItems: "center",
    "& > .MuiFormGroup-root": {
      width: "100%",
    },
  },
});

const ordenacaoOptions = [
  {
    Id: 1,
    Nome: "Código do produto Descendente",
  },
  {
    Id: 2,
    Nome: "Maiores Qtd. em Estoque",
  },
  {
    Id: 3,
    Nome: "Menores Qtd. em Estoque",
  },
  {
    Id: 4,
    Nome: "Nome Ascendente",
  },
];

function ConsultaEstoque() {
  const snackbar = useSnackbar();
  const { setLocalStorageActions, setLocalStorageApiProblem } = useLogs();
  const classes = { ...useStyles(), ...useCustomStyles() };

  const [structureFilters, setStructureFilters] = useState({});
  const [structureLoading, setStructureLoading] = useState([]);
  const { values, handleChange, errors } = useForm({
    ean: "",
    codProduto: "",
    qtdPecasIni: "",
    qtdPecasFim: "",
    ordenacao: 1,
  });
  const [estoque, setEstoque] = useState(null);
  const [totalPaginas, setTotalPaginas] = useState(null);
  const [totalRegistros, setTotalRegistros] = useState(null);
  const [currentPage, setCurrentPage] = useState(null);
  const [currentSizePerPage, setCurrentSizePerPage] = useState(null);
  const [searching, setSearching] = useState(false);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [clearFilters, setClearFilters] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const [hasNoBrand, setHasNoBrand] = useState(false);

  const isMobile900 = IsMobile900();
  const isMobile = IsMobile();

  const toggleUploadDialog = useCallback(() => {
    setOpenUploadDialog(!openUploadDialog);
  }, [openUploadDialog]);

  const searchEstoque = useCallback(
    async (e, page = 1, pageSize = 25) => {
      setSearching(true);
      setCancelLoading(false);
      setErrorMessage(false);

      await Api.get(
        "list-stock-level",
        "stock",
        UtilsFncs.RestParamsComposer(structureFilters, "?ean=" + values.ean) +
          "&codProduto=" +
          values.codProduto +
          "&qtdPecasIni=" +
          values.qtdPecasIni +
          "&qtdPecasFim=" +
          values.qtdPecasFim +
          "&ordenacao=" +
          values.ordenacao +
          "&page=" +
          (page - 1) +
          "&pageSize=" +
          pageSize,
        true
      )
        .then((data) => {
          setEstoque(data.Result.Itens);
          setTotalPaginas(data.Result.TotalDePaginas);
          setTotalRegistros(data.Result.TotalRegistros);
        })
        .catch((error) => {
          console.log(error);
          setErrorMessage(true);
          setLocalStorageApiProblem({
            idMenuAccess: "109",
            screen: "Consulta Estoque",
            problem: `Erro na chamada do endpoint list-stock-level - ${error}`,
          });
        })
        .finally(() => {
          setCurrentPage(page);
          setCurrentSizePerPage(pageSize);
          setSearching(false);
        });
    },
    [
      structureFilters,
      values.codProduto,
      values.ean,
      values.ordenacao,
      values.qtdPecasFim,
      values.qtdPecasIni,
    ]
  );

  const checkUniqueFieldsIsNotUsed = useCallback(() => {
    return (
      MaskAndValid.isNullOrEmpty(values.ean) &&
      MaskAndValid.isNullOrEmpty(values.codProduto)
    );
  }, [values.codProduto, values.ean]);

  const handleClearFilters = () => {
    setClearFilters(true);
    setSearching(true);
    setStructureLoading(true);
    handleChange({
      target: {
        name: "ean",
        value: "",
      },
    });
    handleChange({
      target: {
        name: "codProduto",
        value: "",
      },
    });
    handleChange({
      target: {
        name: "ordenacao",
        value: 1,
      },
    });
    handleChange({
      target: {
        name: "qtdPecasIni",
        value: "",
      },
    });
    handleChange({
      target: {
        name: "qtdPecasFim",
        value: "",
      },
    });
    setSearching(false);
    setStructureLoading(false);
  };

  const handleStopSearching = () => {
    setCancelLoading(true);
    setSearching(false);
    setEstoque(null);
  };

  const handleVerifyRequiredInputs = () => {
    if (structureFilters.idMarca) {
      setLocalStorageActions({
        nomeTela: "Consulta Estoque",
        idMenu: "109",
        acao: "Clicou para Pesquisar",
      });
      return searchEstoque();
    } else {
      if (!structureFilters.idMarca) {
        setHasNoBrand(true);
      }
    }
  };

  useEffect(() => {
    if (errorMessage && !cancelLoading) {
      snackbar.showMessage(
        "Ocorreu um erro ao buscar estoque. Verifique os parâmetros informados e/ou tente novamente mais tarde.",
        {
          severity: "error",
        }
      );
    }
  }, [cancelLoading, errorMessage]);

  useEffect(() => {
    if (structureFilters.idMarca) {
      setHasNoBrand(false);
    }

    if (
      (structureFilters?.idMarca === null &&
        structureFilters?.idCanal === "" &&
        structureFilters?.idRepresentante === "" &&
        structureFilters?.idGrupoEconomico === "") ||
      values.codProduto === ""
    ) {
      setClearFilters(false);
    }
  }, [structureFilters, values]);

  return (
    <>
      <HeaderClientes section="Consulta de Estoque" subsection="Estoque" />
      <Container style={{ marginTop: "-4.5rem" }} maxWidth={false}>
        <Grid container>
          <Grid item xs={12}>
            <Card classes={{ root: classes.cardRoot }}>
              <CardHeader
                className={classes.cardHeader}
                title="Filtros"
                titleTypographyProps={{
                  component: Box,
                  marginBottom: "0!important",
                  variant: "h3",
                }}
              ></CardHeader>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <StructureFields
                    clearFilters={clearFilters}
                    onStructureFieldsChange={setStructureFilters}
                    disableAll={searching}
                    onStructureFieldsLoad={setStructureLoading}
                    fieldsOrder={["economicGroup", "store"]}
                    requiredFields={["brand"]}
                  />
                  <Grid item xs={12}>
                    <Divider style={{ margin: "0.5rem 0" }} />
                  </Grid>

                  <Grid item xs={isMobile ? 12 : isMobile900 ? 6 : 3}>
                    <InputText
                      label="Código de Barras do Produto"
                      type="number"
                      value={values.ean}
                      onChange={(e) => {
                        handleChange({
                          target: {
                            name: "ean",
                            value: e.target.value.slice(0, 13),
                          },
                        });
                      }}
                      disable={searching}
                    />
                  </Grid>

                  <Grid item xs={isMobile ? 12 : isMobile900 ? 6 : 2}>
                    <InputText
                      label="Código do Produto"
                      type="number"
                      value={values.codProduto}
                      onChange={(e) => {
                        handleChange({
                          target: {
                            name: "codProduto",
                            value: e.target.value.slice(0, 13),
                          },
                        });
                      }}
                      disable={searching}
                    />
                  </Grid>

                  <Grid item xs={isMobile ? 12 : isMobile900 ? 6 : 4}>
                    <FormLabel>Quantidade de peças em Estoque</FormLabel>
                    <Box display="flex">
                      <FormGroup style={{ width: "100%" }}>
                        <Box display="flex" alignItems="center">
                          <Box
                            component={Typography}
                            variant="body2"
                            marginLeft="0.4rem!important"
                            marginRight="0.4rem!important"
                          >
                            De:
                          </Box>
                          <TextField
                            name="qtdPecasIni"
                            type="number"
                            variant="outlined"
                            disabled={searching}
                            value={values.qtdPecasIni}
                            onChange={handleChange}
                            fullWidth
                          />
                        </Box>
                      </FormGroup>
                      <FormGroup style={{ width: "100%" }}>
                        <Box display="flex" alignItems="center">
                          <Box
                            component={Typography}
                            variant="body2"
                            marginLeft="0.4rem!important"
                            marginRight="0.4rem!important"
                          >
                            Até:
                          </Box>
                          <TextField
                            name="qtdPecasFim"
                            type="number"
                            variant="outlined"
                            disabled={searching}
                            value={values.qtdPecasFim}
                            onChange={handleChange}
                            fullWidth
                          />
                        </Box>
                      </FormGroup>
                    </Box>
                  </Grid>

                  <Grid item xs={isMobile ? 12 : isMobile900 ? 6 : 3}>
                    <AutoCompleteField
                      options={ordenacaoOptions}
                      label="Ordenar por"
                      value={
                        ordenacaoOptions.find(
                          (opt) => opt.Id === values.ordenacao
                        ) || ordenacaoOptions[0]
                      }
                      onChange={(option) => {
                        handleChange({
                          target: {
                            name: "ordenacao",
                            value: option?.Id || 1,
                          },
                        });
                      }}
                      disabled={searching}
                      optionLabelKey="Nome"
                      optionIdKey="Id"
                    />
                  </Grid>

                  <Grid item xs={isMobile ? 12 : isMobile900 && 6}>
                    <Button
                      style={
                        isMobile || isMobile900
                          ? {
                              width: "100%",
                              marginBottom: "0.75rem",
                              marginTop: "0.75rem",
                            }
                          : { marginBottom: "0rem" }
                      }
                      variant="contained"
                      onClick={handleVerifyRequiredInputs}
                      disabled={
                        searching ||
                        Object.keys(errors).length > 0 ||
                        structureLoading.length > 0
                      }
                    >
                      {searching ? "Pesquisando..." : "Pesquisar"}
                    </Button>
                  </Grid>

                  <Grid
                    item
                    xs={isMobile ? 12 : isMobile900 && 6}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <ClearButton
                      disable={searching || structureLoading.length > 0}
                      onClick={() => handleClearFilters()}
                      fullWidth={true}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            <LoadingData
              searching={searching}
              onClick={() => handleStopSearching()}
              classes={classes.root}
              label="Buscando dados..."
            />
          </Grid>
        </Grid>
        {!searching && estoque && !cancelLoading ? (
          <TableEstoque
            dataTable={estoque}
            totalPaginas={totalPaginas}
            totalRegistros={totalRegistros}
            searchEstoque={searchEstoque}
            currentPage={currentPage}
            currentSizePerPage={currentSizePerPage}
            toggleUploadDialog={toggleUploadDialog}
          />
        ) : null}
        {openUploadDialog && (
          <DialogUploadEstoque
            isMobile={isMobile}
            open={openUploadDialog}
            onClose={toggleUploadDialog}
          />
        )}
      </Container>
    </>
  );
}

export default ConsultaEstoque;
